"use client";

import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Star, Telescope, ChevronRight, Calendar, Zap } from 'lucide-react';

interface SidebarProps {
  activeSection?: string;
  onSectionChange?: (section: string) => void;
  onLinkClick?: () => void;
}

export function AboutSidebar({ activeSection, onSectionChange, onLinkClick }: SidebarProps) {
  const router = useRouter();
  const sidebarItems = [
    {
      id: 'overview',
      label: '⭐ Genel Bakış',
      description: 'AXTROLOG hakkında temel bilgiler',
      href: '/about'
    },
    {
      id: 'astrology',
      label: '🔭 Astroloji',
      description: 'Ev sistemleri ve disiplinler',
      href: '/about/astrology'
    },
    {
      id: 'tarot',
      label: '🃏 Tarot',
      description: 'Sistem açıklamaları',
      href: '/about/tarot'
    },
    {
      id: 'usage-framework',
      label: '📌 Kullanım Çerçevesi',
      description: 'Koşullar ve sınırlamalar',
      href: '/about/usage-framework'
    }
  ];

  return (
    <div className="w-80 bg-slate-900 border-r border-slate-800 overflow-y-auto">
      <div className="p-6">
        <Link href="/" onClick={() => onLinkClick?.()} className="flex items-center gap-3 mb-8 group">
          <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
            <Star className="h-4 w-4 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-100">AXTROLOG</h2>
            <p className="text-xs text-slate-400">Nedir?</p>
          </div>
        </Link>

        <div className="space-y-4">
          {sidebarItems.map((item) => {
            const isActive = activeSection === item.id;

            const handleClick = () => {
              console.log('Link clicked:', item.label, 'href:', item.href);
              onSectionChange?.(item.id);
              onLinkClick?.();
              console.log('Navigating to:', item.href);
              // Navigate after callbacks
              setTimeout(() => router.push(item.href), 100);
            };

            return (
              <div key={item.id} className="mb-4 last:mb-0">
                <button onClick={handleClick} className="w-full text-left">
                  <div
                    className={`p-4 rounded-lg border transition-all duration-200 ${
                      isActive
                        ? 'bg-blue-500/10 border-blue-500/50 text-blue-300'
                        : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:border-slate-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">{item.label}</h3>
                        <p className="text-sm text-slate-400 mt-1">{item.description}</p>
                      </div>
                      <ChevronRight className={`h-4 w-4 transition-transform ${
                        isActive ? 'rotate-90' : ''
                      }`} />
                    </div>
                  </div>
                </button>
              </div>
            );
          })}

          {/* Quick Links */}
          <div className="pt-8 mt-8 border-t border-slate-800">
            <h4 className="text-sm font-medium text-slate-400 mb-5">Hızlı Erişim</h4>
            <div className="space-y-3">
              {/* Astrology Links */}
              <div className="text-xs text-slate-500 mb-3 px-2 font-medium">🔭 Astroloji</div>
              <button onClick={() => { console.log('Quick link clicked: Ev Sistemleri'); onSectionChange?.('astrology'); onLinkClick?.(); setTimeout(() => router.push('/about/astrology/house-systems'), 100); }} className="block w-full text-left p-3 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-blue-500 rounded flex items-center justify-center">
                    <Calendar className="h-3 w-3 text-white" />
                  </div>
                  <span className="text-sm text-slate-300">Ev Sistemleri</span>
                </div>
              </button>
              <button onClick={() => { console.log('Quick link clicked: Astroloji Disiplinleri'); onSectionChange?.('astrology'); onLinkClick?.(); setTimeout(() => router.push('/about/astrology/disciplines'), 100); }} className="block w-full text-left p-3 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-purple-500 rounded flex items-center justify-center">
                    <Telescope className="h-3 w-3 text-white" />
                  </div>
                  <span className="text-sm text-slate-300">Astroloji Disiplinleri</span>
                </div>
              </button>
              <button onClick={() => { console.log('Quick link clicked: Gelişmiş Sistemler'); onSectionChange?.('astrology'); onLinkClick?.(); setTimeout(() => router.push('/about/astrology/advanced-systems'), 100); }} className="block w-full text-left p-3 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-indigo-500 rounded flex items-center justify-center">
                    <Zap className="h-3 w-3 text-white" />
                  </div>
                  <span className="text-sm text-slate-300">Gelişmiş Sistemler</span>
                </div>
              </button>

              {/* Tarot Links */}
              <div className="text-xs text-slate-500 mb-3 px-2 pt-5 font-medium">🃏 Tarot</div>
              <button onClick={() => { console.log('Quick link clicked: Tarot Sistemleri'); onSectionChange?.('tarot'); onLinkClick?.(); setTimeout(() => router.push('/about/tarot/systems'), 100); }} className="block w-full text-left p-3 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-pink-500 rounded flex items-center justify-center">
                    <Star className="h-3 w-3 text-white" />
                  </div>
                  <span className="text-sm text-slate-300">Tarot Sistemleri</span>
                </div>
              </button>
              <button onClick={() => { console.log('Quick link clicked: Yorumlama Teknikleri'); onSectionChange?.('tarot'); onLinkClick?.(); setTimeout(() => router.push('/about/tarot/interpretation-techniques'), 100); }} className="block w-full text-left p-3 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-teal-500 rounded flex items-center justify-center">
                    <Star className="h-3 w-3 text-white" />
                  </div>
                  <span className="text-sm text-slate-300">Yorumlama Teknikleri</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}